import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { MapSelection } from "./serviceMap";

/** The query param that carries the map's selection (declared in `DETAIL_ROUTES`). */
export const MAP_NODE_PARAM = "node";

/** The firewall is the default, so it never needs the param: `?node=` is for the rest. */
const toParam = (selection: MapSelection | null) => (selection === null || selection === "firewall" ? null : selection);
const fromParam = (param: string | null): MapSelection => param ?? "firewall";

/**
 * The map's selection, rebuilt from the URL (CTX-1265, CTX-773): `?node=remote` opens the
 * view on Remote access; no param opens it on the firewall. The page holds the selection
 * and writes the URL after it (replace, so selecting nodes fills no history); a param that
 * changes from outside — Back, a view switch — is adopted. Closing the inspector drops the
 * param, so a reload opens on the firewall again.
 */
export function useMapSelection(): [MapSelection | null, (next: MapSelection | null) => void] {
    const [searchParams, setSearchParams] = useSearchParams();
    const param = searchParams.get(MAP_NODE_PARAM);
    const [selection, setSelection] = useState<MapSelection | null>(() => fromParam(param));
    // The last param this hook wrote: seeing it come back is our own echo, not a navigation.
    const written = useRef<string | null>(param);

    useEffect(() => {
        if (param === written.current) return;
        written.current = param;
        setSelection(fromParam(param));
    }, [param]);

    const select = useCallback(
        (next: MapSelection | null) => {
            setSelection(next);
            const nextParam = toParam(next);
            if (nextParam === written.current) return;
            written.current = nextParam;
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    if (nextParam === null) params.delete(MAP_NODE_PARAM);
                    else params.set(MAP_NODE_PARAM, nextParam);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    return [selection, select];
}
